// Deterministic string → colour mapping for tag-style chips (categories,
// sub-categories, labels, taxonomy badges).
//
// The guarantee: the same string ALWAYS maps to the same colour, across
// renders, across pages, across sessions. That means "Utility Bills" is
// recognisable at a glance in any list — the colour is part of its
// identity, not a per-row accident.
//
// The palette is curated to:
//   - Avoid hues already owned by semantic UI states: warning orange
//     (Pending), success green (Approved), error red (Rejected), neutral
//     grey (Reversed). Reusing those would create false visual signals.
//   - Stay readable at 8% alpha against a white background.
//   - Give enough spread that even catalogs with 15+ categories feel
//     visually distinct — not perfect uniqueness, but strong anchoring.

const PALETTE: readonly string[] = [
  '#5B6CFF', // indigo
  '#0EA5E9', // sky blue
  '#06B6D4', // cyan
  '#14B8A6', // teal
  '#8B5CF6', // violet
  '#A855F7', // purple
  '#D946EF', // fuchsia
  '#EC4899', // pink
  '#F43F5E', // rose
  '#4F46E5', // deep indigo
  '#2563EB', // royal blue
  '#0891B2', // dark cyan
] as const;

/** FNV-1a 32-bit — fast, decent spread for short strings. */
function hash(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/**
 * Pick a stable colour for a tag name. Returns a neutral slate if the
 * input is empty/null so callers can safely pass through optional strings.
 * Input is case-folded and trimmed so "utility bills" and "Utility Bills "
 * collapse to the same colour.
 */
export function tagColor(name: string | undefined | null): string {
  if (!name || !name.trim()) return '#64748B'; // slate
  return PALETTE[hash(name.trim().toLowerCase()) % PALETTE.length];
}
